import { TZDate } from "@date-fns/tz";
import {
  format,
  intlFormatDistance,
  isValid,
  parseISO,
  type Locale as DateFnsLocale,
} from "date-fns";
import { enUS } from "date-fns/locale";

import { getLocale, type Locale } from "#/paraglide/runtime";

/** 月份和星期名跟着 Paraglide 的语言走；Paraglide 加了语言而这里没补，类型检查不过。 */
const DATE_FNS_LOCALES: Record<Locale, DateFnsLocale> = { en: enUS };

/** 后台的时间一律按工作区时区显示（PRD 4.1），不跟浏览器时区走。 */
export function formatWorkspaceDate(instant: number, timeZone: string): string {
  return format(new TZDate(instant, timeZone), "EEE, MMM d, yyyy", {
    locale: DATE_FNS_LOCALES[getLocale()],
  });
}

/**
 * 时刻要带上时区缩写，否则商家看不出这是店铺时间还是自己电脑的时间。
 * date-fns 的 `zzz` 只给得出 GMT-7 这种偏移，缩写只有 Intl 有，这一处因此用 Intl。
 */
export function formatWorkspaceTime(instant: number, timeZone: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  }).format(instant);
}

/** 解析不出来就返回 null，让调用方把后端给的原串照原样摆出去，而不是显示 Invalid Date。 */
function readWorkspaceInstant(
  isoDateTime: string,
  timeZone: string,
): TZDate | null {
  const parsed = parseISO(isoDateTime);

  return isValid(parsed) ? new TZDate(parsed, timeZone) : null;
}

/** 接口给的是 ISO 串，回放头要的是 `Aug 25, 9:04 AM` 这种短日期加时刻。 */
export function formatWorkspaceDateTime(
  isoDateTime: string,
  timeZone: string,
): string {
  const at = readWorkspaceInstant(isoDateTime, timeZone);

  return at === null
    ? isoDateTime
    : format(at, "MMM d, h:mm a", { locale: DATE_FNS_LOCALES[getLocale()] });
}

/** 回放里逐条消息只出时刻：日期在面板顶部已经说过一次，每条再带一遍全是噪音。 */
export function formatWorkspaceClock(
  isoDateTime: string,
  timeZone: string,
): string {
  const at = readWorkspaceInstant(isoDateTime, timeZone);

  return at === null
    ? isoDateTime
    : format(at, "h:mm a", { locale: DATE_FNS_LOCALES[getLocale()] });
}

/**
 * 会话列表的 `4 minutes ago` / `yesterday`；基准时刻同样落在店铺时区，两边口径一致。
 * locale 不能省：Intl 缺了它就跟浏览器语言走，中文浏览器会出「8分钟前」。
 */
export function formatWorkspaceRelative(
  isoDateTime: string,
  timeZone: string,
): string {
  const at = readWorkspaceInstant(isoDateTime, timeZone);

  return at === null
    ? isoDateTime
    : intlFormatDistance(at, new TZDate(Date.now(), timeZone), {
        locale: getLocale(),
      });
}
